import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "./supabase/auth";
import { selectAll } from "./supabase/client";
import { logActivity } from "./activityLog";
import { useTranslation } from 'react-i18next';
import {
  computeFinishedGood,
  scaleToServing,
  CalcIngredient,
} from "./calculations";

/* ── Types ─────────────────────────────────────────────────────────────────── */

interface Formula {
  formula_code: string;
  display_name: string | null;
  owner_name: string | null;
  formula_date: string | null;
  piece_weight_g: number | null;
  serving_size_g: number | null;
  dough_ratio_pct: number | null;
  notes: string | null;
  is_active: boolean;
  source_formula_code: string | null;
  created_at: string | null;
  updated_at: string | null;
  updated_by_email: string | null;
  ratio_mode: string | null;
  final_moisture_mode: string | null;
  final_moisture_whole: number | null;
  final_moisture_dough: number | null;
  final_moisture_filling: number | null;
}

interface IngredientRow {
  id: string;
  common_name: string;
  is_active: boolean | null;
  protein_per_100g: number;
  digestibility: number;
  cost_per_kg: number;
  fat: number;
  satfat: number;
  transfat: number;
  cholesterol: number;
  carbs: number;
  fiber: number;
  sugar: number;
  sodium: number;
  moisture: number;
  vitamin_d: number;
  calcium: number;
  iron: number;
  potassium: number;
  color: string;
  aa_his: number;
  aa_ile: number;
  aa_leu: number;
  aa_lys: number;
  aa_mc: number;
  aa_pt: number;
  aa_thr: number;
  aa_trp: number;
  aa_val: number;
  fa_ala: number | null;
  fa_la: number | null;
  fa_oleic: number | null;
  fa_palmitic: number | null;
  fa_stearic: number | null;
  fiber_insoluble: number | null;
  fiber_soluble: number | null;
  resistant_starch: number | null;
}

function toCalcIngredient(r: IngredientRow): CalcIngredient {
  return {
    protein_per_100g: r.protein_per_100g,
    digestibility: r.digestibility,
    cost_per_kg: r.cost_per_kg,
    fat: r.fat,
    satfat: r.satfat,
    transfat: r.transfat,
    cholesterol: r.cholesterol,
    carbs: r.carbs,
    fiber: r.fiber,
    sugar: r.sugar,
    sodium: r.sodium,
    moisture: r.moisture,
    vitaminD: r.vitamin_d,
    calcium: r.calcium,
    iron: r.iron,
    potassium: r.potassium,
    color: r.color || "#888888",
    amino_acids: {
      histidine: r.aa_his,
      isoleucine: r.aa_ile,
      leucine: r.aa_leu,
      lysine: r.aa_lys,
      methionine_cysteine: r.aa_mc,
      phenylalanine_tyrosine: r.aa_pt,
      threonine: r.aa_thr,
      tryptophan: r.aa_trp,
      valine: r.aa_val,
    },
    fa_ala: r.fa_ala ?? 0,
    fa_la: r.fa_la ?? 0,
    fa_oleic: r.fa_oleic ?? 0,
    fa_palmitic: r.fa_palmitic ?? 0,
    fa_stearic: r.fa_stearic ?? 0,
    fiber_insoluble: r.fiber_insoluble ?? 0,
    fiber_soluble: r.fiber_soluble ?? 0,
    resistant_starch: r.resistant_starch ?? 0,
  };
}

interface Component {
  formula_code: string;
  component: string;
  ingredient_id: string | null;
  grams: number | null;
  pct_of_component: number | null;
  row_type: string | null;
}

interface ColDef {
  key: string;
  label: string;
  align: "left" | "right";
  defaultWidth: number;
  alwaysVisible?: boolean;
}

type SortDir = "asc" | "desc";
type Preset = "all" | "high_protein" | "with_lp90" | "with_pea" | "recent";

const mono = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

/* ── Column definitions ─────────────────────────────────────────────────── */

function getAllColumns(t: (key: string) => string): ColDef[] {
  return [
    { key: "_open", label: "", align: "left", defaultWidth: 68, alwaysVisible: true },
    { key: "_star", label: "\u2605", align: "left", defaultWidth: 36, alwaysVisible: true },
    { key: "formula_code", label: t('formulaList.formulaCode'), align: "left", defaultWidth: 120, alwaysVisible: true },
    { key: "display_name", label: t('formulaList.displayName'), align: "left", defaultWidth: 180, alwaysVisible: true },
    { key: "owner_name", label: t('formulaList.owner'), align: "left", defaultWidth: 120 },
    { key: "notes", label: t('formulaList.notes'), align: "left", defaultWidth: 240 },
    { key: "updated_at", label: t('formulaList.lastUpdated'), align: "left", defaultWidth: 140 },
    { key: "_comment_count", label: t('formulaList.comments'), align: "right", defaultWidth: 70 },
    // Calculated nutrient columns
    { key: "_protein", label: t('formulaList.proteinPerServing'), align: "right", defaultWidth: 180 },
    { key: "_fat", label: t('formulaList.fatPerServing'), align: "right", defaultWidth: 180 },
    { key: "_carbs", label: t('formulaList.carbsPerServing'), align: "right", defaultWidth: 180 },
    { key: "_fiber", label: t('formulaList.fiberPerServing'), align: "right", defaultWidth: 180 },
    { key: "_sugar", label: t('formulaList.sugarPerServing'), align: "right", defaultWidth: 180 },
    { key: "_sodium", label: t('formulaList.sodiumPerServing'), align: "right", defaultWidth: 190 },
    { key: "_cost", label: t('formulaList.costPerServing'), align: "right", defaultWidth: 120 },
    { key: "_dough_total", label: t('formulaList.doughTotal'), align: "right", defaultWidth: 100 },
    { key: "_filling_total", label: t('formulaList.fillingTotal'), align: "right", defaultWidth: 100 },
    // Static hidden columns
    { key: "serving_size_g", label: t('formulaList.serving'), align: "right", defaultWidth: 90 },
    { key: "created_at", label: t('formulaList.createdAt'), align: "left", defaultWidth: 140 },
    // Actions
    { key: "_actions", label: "", align: "left", defaultWidth: 40, alwaysVisible: true },
  ];
}

const DEFAULT_VISIBLE = new Set([
  "_open", "_star", "formula_code", "display_name", "owner_name", "formula_date",
  "notes", "updated_at", "_comment_count", "_protein", "_fiber", "_actions",
]);

/* ── Starred (localStorage) ─────────────────────────────────────────────── */

const STARRED_KEY = "starred_formulas";

function getStarred(): Set<string> {
  try {
    const raw = localStorage.getItem(STARRED_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch { return new Set(); }
}

function setStarredStorage(s: Set<string>) {
  localStorage.setItem(STARRED_KEY, JSON.stringify(Array.from(s)));
}

/* ── Cell comments ─────────────────────────────────────────────────────── */

interface CellComment {
  id: number;
  formula_code: string;
  field_name: string;
  row_id: string | null;
  comment_text: string;
  author_email: string;
  created_at: string;
}

function authorAbbrev(email: string): string {
  const local = email.split("@")[0] || "";
  const parts = local.split(".");
  return parts.map((p) => p.charAt(0).toLowerCase()).join("");
}

function listCommentKey(formulaCode: string, colKey: string): string {
  return formulaCode + "|" + colKey;
}

/* ── Helpers ─────────────────────────────────────────────────────────────── */

function formatDatetime(dt: string | null): string {
  if (!dt) return "-";
  try {
    const d = new Date(dt);
    const y = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    const da = String(d.getDate()).padStart(2, "0");
    const h = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return y + "-" + mo + "-" + da + " " + h + ":" + mi;
  } catch { return "-"; }
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "-";
  try {
    const d = new Date(dateStr);
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return String(d.getDate()).padStart(2, "0") + " " + months[d.getMonth()] + " " + d.getFullYear();
  } catch { return "-"; }
}

function compareValues(a: unknown, b: unknown, dir: SortDir): number {
  const nullA = a === null || a === undefined;
  const nullB = b === null || b === undefined;
  if (nullA && nullB) return 0;
  if (nullA) return 1;
  if (nullB) return -1;
  const mul = dir === "asc" ? 1 : -1;
  if (typeof a === "number" && typeof b === "number") return (a - b) * mul;
  return String(a).localeCompare(String(b)) * mul;
}

function n2(v: number | null | undefined): string {
  return v != null ? v.toFixed(2) : "-";
}

/* ── Calculated data per formula ── */

interface CalcData {
  protein: number;
  fat: number;
  carbs: number;
  fiber: number;
  sugar: number;
  sodium: number;
  cost: number;
  servingSizeG: number;
  doughGrams: number;
  fillingGrams: number;
}

function computeCalcData(
  formula: Formula,
  byFormula: Record<string, Component[]>,
  idToName: Record<string, string>,
  calcIngMap: Record<string, CalcIngredient>,
): CalcData {
  const allComps = byFormula[formula.formula_code] ?? [];
  const comps = allComps.filter((c) => (c.row_type ?? "ingredient") === "ingredient");

  // Section grams totals for display
  const secGrams: Record<string, number> = {};
  for (const c of comps) secGrams[c.component] = (secGrams[c.component] ?? 0) + (c.grams ?? 0);

  // Build dough / filling formula maps: ingredient name → pct (0-100)
  const doughFormula: Record<string, number> = {};
  const fillingFormula: Record<string, number> = {};
  for (const c of comps) {
    if (!c.ingredient_id || c.pct_of_component == null) continue;
    const name = idToName[c.ingredient_id] || c.ingredient_id;
    const pct = c.pct_of_component * 100; // stored as 0-1 fraction
    if (c.component === "DOUGH") doughFormula[name] = pct;
    else if (c.component === "FILLING") fillingFormula[name] = pct;
  }

  const servingSizeG = formula.serving_size_g ?? 84;
  const doughRatioPct = formula.dough_ratio_pct ?? 100;

  // Use the same calculation as NFP / FormulaCompare
  const result = computeFinishedGood(
    doughFormula,
    fillingFormula,
    doughRatioPct,
    formula.ratio_mode || "as_mixed",
    formula.final_moisture_mode,
    formula.final_moisture_whole,
    formula.final_moisture_dough,
    formula.final_moisture_filling,
    calcIngMap,
  );

  const n = result.nutrients;

  return {
    protein: scaleToServing(n.protein, servingSizeG),
    fat: scaleToServing(n.fat, servingSizeG),
    carbs: scaleToServing(n.carbs, servingSizeG),
    fiber: scaleToServing(n.fiber, servingSizeG),
    sugar: scaleToServing(n.sugar, servingSizeG),
    sodium: scaleToServing(n.sodium, servingSizeG),
    cost: scaleToServing(n.cost, servingSizeG),
    servingSizeG,
    doughGrams: secGrams["DOUGH"] ?? 0,
    fillingGrams: secGrams["FILLING"] ?? 0,
  };
}

/* ════════════════════════════════════════════════════════════════════════════ */

export default function FormulaListScreen({
  onSelectFormula,
  onAddFormula,
}: {
  onSelectFormula?: (code: string) => void;
  onAddFormula?: () => void;
}) {
  const { t } = useTranslation();
  const ALL_COLUMNS = useMemo(() => getAllColumns(t), [t]);
  /* ── State ── */
  const [formulas, setFormulas] = useState<Formula[]>([]);
  const [allComponents, setAllComponents] = useState<Component[]>([]);
  const [ingredients, setIngredients] = useState<IngredientRow[]>([]);
  const [commentCounts, setCommentCounts] = useState<Record<string, number>>({});
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [sortCol, setSortCol] = useState<string>("formula_code");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [starred, setStarred] = useState<Set<string>>(getStarred);
  const [preset, setPreset] = useState<Preset>("all");
  const [hiddenCols, setHiddenCols] = useState<Set<string>>(() => {
    const hidden = new Set<string>();
    const cols = getAllColumns((k: string) => k);
    for (const c of cols) if (!DEFAULT_VISIBLE.has(c.key)) hidden.add(c.key);
    return hidden;
  });
  const [colMenuOpen, setColMenuOpen] = useState(false);
  const [colWidths, setColWidths] = useState<Record<string, number>>(() => {
    const w: Record<string, number> = {};
    const cols = getAllColumns((k: string) => k);
    for (const c of cols) w[c.key] = c.defaultWidth;
    return w;
  });
  const resizeRef = useRef<{ key: string; startX: number; startW: number } | null>(null);
  const [comments, setComments] = useState<CellComment[]>([]);
  const [currentUserEmail, setCurrentUserEmail] = useState<string | null>(null);
  const [commentPopover, setCommentPopover] = useState<{
    x: number; y: number; formulaCode: string; colKey: string;
  } | null>(null);
  const [newCommentText, setNewCommentText] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  /* ── Data loading ── */
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [fRes, cRes, iRes, ccRes] = await Promise.all([
        supabase.from("formulas").select("*").eq("is_active", true).order("formula_code"),
        selectAll<Component>("formula_components", "formula_code, component, ingredient_id, grams, pct_of_component, row_type"),
        supabase.from("ingredients").select("*").eq("is_active", true),
        supabase.from("formula_comments").select("*").order("created_at", { ascending: true }),
      ]);
      // A failed query must not render as the misleading "No formulas found"
      // empty state — surface the first error instead.
      const firstError = fRes.error || iRes.error || ccRes.error;
      if (firstError) {
        setLoadError(firstError.message);
        return;
      }
      setFormulas((fRes.data as Formula[]) || []);
      setAllComponents(cRes || []);
      setIngredients((iRes.data as IngredientRow[]) || []);
      // Store full comments and count per formula
      const allComments = (ccRes.data as CellComment[]) || [];
      setComments(allComments);
      const cc: Record<string, number> = {};
      for (const r of allComments) {
        cc[r.formula_code] = (cc[r.formula_code] || 0) + 1;
      }
      setCommentCounts(cc);
    } catch (e: any) {
      setLoadError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Fetch current user email for comment ownership
  useEffect(() => {
    (async () => {
      const { data } = await supabase.auth.getUser();
      setCurrentUserEmail(data?.user?.email ?? null);
    })();
  }, []);

  /* ── Lookup maps ── */
  const { calcIngMap, idToName } = useMemo(() => {
    const cMap: Record<string, CalcIngredient> = {};
    const nameMap: Record<string, string> = {};
    for (const row of ingredients) {
      const calc = toCalcIngredient(row);
      cMap[row.common_name] = calc;
      nameMap[row.id] = row.common_name;
      if (row.id !== row.common_name) {
        cMap[row.id] = calc;
      }
    }
    return { calcIngMap: cMap, idToName: nameMap };
  }, [ingredients]);

  const byFormula = useMemo(() => {
    const m: Record<string, Component[]> = {};
    for (const c of allComponents) {
      if (!m[c.formula_code]) m[c.formula_code] = [];
      m[c.formula_code].push(c);
    }
    return m;
  }, [allComponents]);

  /* ── Comment helpers ── */
  const listCommentsByKey = useMemo(() => {
    const m = new Map<string, CellComment[]>();
    for (const c of comments) {
      if (c.row_id !== "__list__") continue;
      const k = listCommentKey(c.formula_code, c.field_name);
      const arr = m.get(k) ?? [];
      arr.push(c);
      m.set(k, arr);
    }
    return m;
  }, [comments]);

  const handleCellContextMenu = useCallback((formulaCode: string, colKey: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCommentPopover({ x: e.clientX, y: e.clientY, formulaCode, colKey });
    setNewCommentText("");
  }, []);

  const deleteComment = useCallback(async (commentId: number) => {
    const { error } = await supabase.from("formula_comments").delete().eq("id", commentId);
    if (!error) {
      setComments((prev) => {
        const removed = prev.find((c) => c.id === commentId);
        const next = prev.filter((c) => c.id !== commentId);
        if (removed) {
          setCommentCounts((cc) => ({
            ...cc,
            [removed.formula_code]: Math.max(0, (cc[removed.formula_code] || 0) - 1),
          }));
        }
        return next;
      });
    } else {
      alert("Failed to delete comment: " + error.message);
    }
  }, []);

  const submitListComment = useCallback(async () => {
    if (!commentPopover || !newCommentText.trim()) return;
    setSubmittingComment(true);
    const { data: userData } = await supabase.auth.getUser();
    const email = userData?.user?.email ?? "anonymous";
    const { data, error } = await supabase.from("formula_comments").insert({
      formula_code: commentPopover.formulaCode,
      field_name: commentPopover.colKey,
      row_id: "__list__",
      comment_text: newCommentText.trim(),
      author_email: email,
    }).select().single();
    if (!error && data) {
      const newComment = data as CellComment;
      setComments((prev) => [...prev, newComment]);
      setCommentCounts((prev) => ({
        ...prev,
        [newComment.formula_code]: (prev[newComment.formula_code] || 0) + 1,
      }));
      setNewCommentText("");
    } else if (error) {
      console.error("Comment insert failed:", error.message);
      alert("Failed to save comment: " + error.message);
    }
    setSubmittingComment(false);
  }, [commentPopover, newCommentText]);

  // Close popover on outside click
  useEffect(() => {
    if (!commentPopover) return;
    const handler = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setCommentPopover(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [commentPopover]);

  /* ── Ingredient set lookups for presets ── */
  const formulasWithIngredient = useCallback((ingredientIds: string[]): Set<string> => {
    const s = new Set<string>();
    const targets = new Set(ingredientIds);
    for (const c of allComponents) {
      if (c.ingredient_id && targets.has(c.ingredient_id)) s.add(c.formula_code);
    }
    return s;
  }, [allComponents]);

  /* ── Calc data (uses each formula's own saved settings) ── */
  const calcMap = useMemo(() => {
    const m: Record<string, CalcData> = {};
    for (const f of formulas) {
      m[f.formula_code] = computeCalcData(f, byFormula, idToName, calcIngMap);
    }
    return m;
  }, [formulas, byFormula, idToName, calcIngMap]);

  /* ── Column resize ── */
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!resizeRef.current) return;
      const { key, startX, startW } = resizeRef.current;
      const newW = Math.max(36, startW + e.clientX - startX);
      setColWidths((prev) => ({ ...prev, [key]: newW }));
    };
    const onUp = () => { resizeRef.current = null; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, []);

  const startResize = (key: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = { key, startX: e.clientX, startW: colWidths[key] ?? 100 };
  };

  /* ── Star toggle ── */
  const toggleStar = useCallback((code: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setStarred((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      setStarredStorage(next);
      return next;
    });
  }, []);

  /* ── Archive formula (soft delete) ──
     Formula numbering is append-only, so nothing is ever destroyed: this clears
     is_active, which is already the list's filter, so the row leaves the screen
     while its components, lineage and history stay intact and recoverable. */
  const deleteFormula = useCallback(async (code: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(t('formulaList.deleteConfirm', { code }))) return;
    // .select() verifies a row was actually updated — under RLS a 0-row
    // PATCH returns 200 with no error, so error alone is not enough.
    const { data, error } = await supabase
      .from("formulas")
      .update({ is_active: false })
      .eq("formula_code", code)
      .select("formula_code");
    if (error) {
      window.alert(`${code}: ${error.message}`);
      return;
    }
    if (!data || data.length === 0) {
      window.alert(`${code}: ${t('formulaList.deleteNoRow', { defaultValue: 'Delete blocked or row missing' })}`);
      return;
    }
    logActivity("formula_delete", { formula_code: code });
    fetchAll();
  }, [fetchAll, t]);

  /* ── Filtering ── */
  const filtered = useMemo(() => {
    let list = formulas;

    // Text search
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((f) =>
        f.formula_code?.toLowerCase().includes(q) || f.display_name?.toLowerCase().includes(q),
      );
    }

    // Preset filters
    if (preset === "high_protein") {
      list = list.filter((f) => (calcMap[f.formula_code]?.protein ?? 0) > 10);
    } else if (preset === "with_lp90") {
      const s = formulasWithIngredient(["WOA_LP90"]);
      list = list.filter((f) => s.has(f.formula_code));
    } else if (preset === "with_pea") {
      const s = formulasWithIngredient(["PEA_PROTEIN", "KR_PEA_PROTEIN_POWDER"]);
      list = list.filter((f) => s.has(f.formula_code));
    }
    return list;
  }, [formulas, search, preset, calcMap, formulasWithIngredient]);

  /* ── Sorting ── */
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      // Starred first
      const sa = starred.has(a.formula_code) ? 0 : 1;
      const sb = starred.has(b.formula_code) ? 0 : 1;
      if (sa !== sb) return sa - sb;

      // "Recently Updated" preset overrides. Pass dir "desc" rather than
      // swapping a/b — swapping also inverts the null-last handling.
      if (preset === "recent") {
        return compareValues(a.updated_at, b.updated_at, "desc");
      }

      // Calculated column sort
      const ca = calcMap[a.formula_code];
      const cb = calcMap[b.formula_code];
      if (sortCol === "_protein") return compareValues(ca?.protein, cb?.protein, sortDir);
      if (sortCol === "_fat") return compareValues(ca?.fat, cb?.fat, sortDir);
      if (sortCol === "_carbs") return compareValues(ca?.carbs, cb?.carbs, sortDir);
      if (sortCol === "_fiber") return compareValues(ca?.fiber, cb?.fiber, sortDir);
      if (sortCol === "_sugar") return compareValues(ca?.sugar, cb?.sugar, sortDir);
      if (sortCol === "_sodium") return compareValues(ca?.sodium, cb?.sodium, sortDir);
      if (sortCol === "_cost") return compareValues(ca?.cost, cb?.cost, sortDir);
      if (sortCol === "_dough_total") return compareValues(ca?.doughGrams, cb?.doughGrams, sortDir);
      if (sortCol === "_filling_total") return compareValues(ca?.fillingGrams, cb?.fillingGrams, sortDir);
      if (sortCol === "_comment_count") return compareValues(commentCounts[a.formula_code] ?? 0, commentCounts[b.formula_code] ?? 0, sortDir);

      return compareValues((a as any)[sortCol], (b as any)[sortCol], sortDir);
    });
  }, [filtered, sortCol, sortDir, starred, calcMap, commentCounts, preset]);

  const handleSort = (col: string) => {
    if (col === "_open" || col === "_star" || col === "_actions") return;
    if (col === sortCol) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortCol(col); setSortDir("asc"); }
  };

  const toggleCol = (key: string) => {
    setHiddenCols((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const visibleCols = ALL_COLUMNS.filter((c) => !hiddenCols.has(c.key));
  const toggleableCols = ALL_COLUMNS.filter((c) => !c.alwaysVisible);

  /* ── Dough/filling ratio sync ── */

  /* ── Cell value rendering ── */
  const getCellValue = (f: Formula, key: string): React.ReactNode => {
    const cd = calcMap[f.formula_code];
    switch (key) {
      case "_star":
        return (
          <span
            onClick={(e) => toggleStar(f.formula_code, e)}
            style={{ cursor: "pointer", color: starred.has(f.formula_code) ? "#eab308" : "#ccc", fontSize: "16px" }}
          >{starred.has(f.formula_code) ? "\u2605" : "\u2606"}</span>
        );
      case "_open":
        return (
          <span style={{ display: "flex", justifyContent: "center" }}>
            <button
              onClick={(e) => { e.stopPropagation(); onSelectFormula?.(f.formula_code); }}
              className="open-formula-btn"
              style={{
                display: "inline-flex", alignItems: "center", gap: "4px",
                padding: "4px 12px", fontSize: "11px", fontWeight: 700,
                background: "linear-gradient(180deg, #60a5fa 0%, #3b82f6 50%, #2563eb 100%)",
                color: "#fff", border: "none",
                borderRadius: "6px", cursor: "pointer", lineHeight: "16px",
                boxShadow: "0 1px 3px rgba(37,99,235,0.4), inset 0 1px 0 rgba(255,255,255,0.25)",
                letterSpacing: "0.3px",
                transition: "all 0.15s ease",
              }}
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
              {t('formulaList.open')}
            </button>
          </span>
        );
      case "_actions":
        return (
          <span style={{ display: "flex", justifyContent: "center" }}>
            <span
              onClick={(e) => deleteFormula(f.formula_code, e)}
              style={{ cursor: "pointer", color: "#b91c1c", fontSize: "13px" }}
              title={t('formulaList.deleteFormula')}
            >{"\uD83D\uDDD1"}</span>
          </span>
        );
      case "formula_code": return f.formula_code;
      case "display_name": return f.display_name || "-";
      case "owner_name": return f.owner_name || "-";
      case "serving_size_g": return f.serving_size_g != null ? f.serving_size_g : "-";
      case "notes": return f.notes || "-";
      case "created_at": return formatDatetime(f.created_at);
      case "updated_at": return formatDatetime(f.updated_at);
      case "_comment_count": return commentCounts[f.formula_code] ?? 0;
      case "_protein": return cd ? `${n2(cd.protein)}g /${cd.servingSizeG}g serving` : "-";
      case "_fat": return cd ? `${n2(cd.fat)}g /${cd.servingSizeG}g serving` : "-";
      case "_carbs": return cd ? `${n2(cd.carbs)}g /${cd.servingSizeG}g serving` : "-";
      case "_fiber": return cd ? `${n2(cd.fiber)}g /${cd.servingSizeG}g serving` : "-";
      case "_sugar": return cd ? `${n2(cd.sugar)}g /${cd.servingSizeG}g serving` : "-";
      case "_sodium": return cd ? `${cd.sodium.toFixed(2)}mg /${cd.servingSizeG}g serving` : "-";
      case "_cost": return n2(cd?.cost);
      case "_dough_total": return n2(cd?.doughGrams);
      case "_filling_total": return n2(cd?.fillingGrams);
      default: return "-";
    }
  };

  const getCellStyle = (f: Formula, key: string): React.CSSProperties => {
    const base: React.CSSProperties = {
      padding: "4px 8px", fontSize: "12px", color: "#000",
      border: "1px solid #333", background: "#fff",
      whiteSpace: "nowrap", lineHeight: "22px", overflow: "hidden", textOverflow: "ellipsis",
    };
    const col = ALL_COLUMNS.find((c) => c.key === key);
    if (col?.align === "right") { base.textAlign = "right"; base.fontFamily = mono; }
    if (key === "formula_code") base.fontFamily = mono;
    if (key === "_open" || key === "_star" || key === "_actions") { base.textAlign = "center"; base.padding = "4px"; }
    if (key === "notes") base.maxWidth = "240px";
    const val = getCellValue(f, key);
    if (val === "-" || val === 0) base.color = "#999";
    return base;
  };

  const thStyle = (col: ColDef): React.CSSProperties => ({
    padding: "4px 8px", textAlign: col.align, fontSize: "11px", fontWeight: 700,
    color: "#000", textTransform: "uppercase", border: "1px solid #333",
    background: "#f0f0f0", whiteSpace: "nowrap", lineHeight: "28px",
    cursor: col.key === "_open" || col.key === "_star" || col.key === "_actions" ? "default" : "pointer",
    userSelect: "none", position: "relative" as const,
    width: colWidths[col.key] + "px", minWidth: "36px",
    // sticky header
    top: 0, zIndex: 1,
  });

  const btnStyle: React.CSSProperties = {
    padding: "4px 10px", fontSize: "12px", fontWeight: 500,
    border: "1px solid #c8c8c8", background: "#fff", cursor: "pointer", color: "#000", lineHeight: "18px",
  };

  const presetBtn = (p: Preset, label: string): React.ReactNode => (
    <button
      key={p}
      style={{
        ...btnStyle,
        background: preset === p ? "#333" : "#fff",
        color: preset === p ? "#fff" : "#000",
        fontWeight: preset === p ? 700 : 500,
      }}
      onClick={() => setPreset(p === preset ? "all" : p)}
    >{label}</button>
  );

  return (
    <div style={{ background: "transparent", padding: "8px", minHeight: "100%" }}>
      <style>{`
        .open-formula-btn:hover {
          background: linear-gradient(180deg, #93bbfd 0%, #60a5fa 50%, #3b82f6 100%) !important;
          box-shadow: 0 2px 6px rgba(37,99,235,0.5), inset 0 1px 0 rgba(255,255,255,0.3) !important;
        }
        .open-formula-btn:active {
          background: linear-gradient(180deg, #3b82f6 0%, #2563eb 50%, #1d4ed8 100%) !important;
          box-shadow: 0 0 2px rgba(37,99,235,0.4), inset 0 1px 3px rgba(0,0,0,0.2) !important;
          transform: translateY(0.5px);
        }
      `}</style>
      {/* ── Comment Popover ── */}
      {commentPopover && (
        <div
          ref={popoverRef}
          style={{
            position: "fixed", left: commentPopover.x, top: commentPopover.y,
            zIndex: 9999, background: "#fff", border: "1px solid #ccc",
            borderRadius: "4px", padding: "8px", width: "260px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)", maxHeight: "240px",
            display: "flex", flexDirection: "column",
          }}
        >
          <div style={{ fontSize: "10px", fontWeight: 700, color: "#666", textTransform: "uppercase", marginBottom: "4px" }}>
            {t('formulaList.comments')}: {commentPopover.colKey} ({commentPopover.formulaCode})
          </div>
          <div style={{ flex: 1, overflowY: "auto", maxHeight: "140px", marginBottom: "6px" }}>
            {(listCommentsByKey.get(listCommentKey(commentPopover.formulaCode, commentPopover.colKey)) ?? []).map((c) => (
              <div key={c.id} style={{ fontSize: "11px", marginBottom: "4px", borderBottom: "1px solid #eee", paddingBottom: "3px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span>
                    <span style={{ fontWeight: 700, color: "#3b82f6" }}>{authorAbbrev(c.author_email)}</span>
                    <span style={{ color: "#999", marginLeft: "4px" }}>
                      {new Date(c.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </span>
                  {currentUserEmail && c.author_email === currentUserEmail && (
                    <span
                      onClick={() => deleteComment(c.id)}
                      style={{ cursor: "pointer", color: "#b91c1c", fontSize: "10px", padding: "0 2px" }}
                      title={t('formulaList.deleteComment')}
                    >{"\u2715"}</span>
                  )}
                </div>
                <div style={{ color: "#333" }}>{c.comment_text}</div>
              </div>
            ))}
            {!(listCommentsByKey.get(listCommentKey(commentPopover.formulaCode, commentPopover.colKey)) ?? []).length && (
              <div style={{ color: "#999", fontSize: "11px" }}>{t('formulaList.noComments')}</div>
            )}
          </div>
          <div style={{ display: "flex", gap: "4px" }}>
            <input
              type="text" placeholder={t('formulaList.addComment')}
              value={newCommentText} onChange={(e) => setNewCommentText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submitListComment(); }}
              style={{ flex: 1, fontSize: "11px", padding: "3px 6px", border: "1px solid #ccc", outline: "none" }}
              autoFocus
            />
            <button
              onClick={submitListComment} disabled={submittingComment || !newCommentText.trim()}
              style={{ fontSize: "11px", padding: "3px 8px", cursor: "pointer", border: "1px solid #3b82f6", background: "#3b82f6", color: "#fff" }}
            >{t('formulaList.add')}</button>
          </div>
        </div>
      )}

      {/* ── Top bar: New Formula + Search + Columns ── */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px", flexWrap: "wrap" }}>
        {onAddFormula && <button style={{ ...btnStyle, fontWeight: 700 }} onClick={onAddFormula}>{t('formulaList.newFormula')}</button>}
        <input
          type="text" placeholder={t('formulaList.searchPlaceholder')}
          value={search} onChange={(e) => setSearch(e.target.value)}
          style={{ background: "#fff", border: "1px solid #c8c8c8", padding: "4px 8px", fontSize: "12px", color: "#000", outline: "none", width: "280px" }}
        />
        <div style={{ position: "relative" }}>
          <button style={btnStyle} onClick={() => setColMenuOpen((o) => !o)}>{t('formulaList.columns')} &#9662;</button>
          {colMenuOpen && (
            <div style={{
              position: "absolute", top: "100%", left: 0, zIndex: 100,
              background: "#fff", color: "#000", border: "1px solid #ccc", padding: "6px 8px",
              maxHeight: "360px", overflowY: "auto", width: "210px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            }}>
              {toggleableCols.map((c) => (
                <label key={c.key} style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "11px", padding: "2px 0", cursor: "pointer" }}>
                  <input type="checkbox" checked={!hiddenCols.has(c.key)} onChange={() => toggleCol(c.key)} />
                  {c.label}
                </label>
              ))}
            </div>
          )}
        </div>
        <span style={{ color: "#666", fontSize: "12px", whiteSpace: "nowrap" }}>
          {search.trim() || preset !== "all"
            ? sorted.length + " " + t('formulaList.of') + " " + formulas.length + " " + t('formulaList.formulas')
            : formulas.length + " " + t('formulaList.formulas')}
        </span>
      </div>


      {/* ── Smart view presets ── */}
      <div style={{ display: "flex", gap: "6px", marginBottom: "8px", flexWrap: "wrap" }}>
        {presetBtn("all", t('formulaList.allFormulas'))}
        {presetBtn("high_protein", t('formulaList.highProtein'))}
        {presetBtn("with_lp90", t('formulaList.withLP90'))}
        {presetBtn("with_pea", t('formulaList.withPeaProtein'))}
        {presetBtn("recent", t('formulaList.recentlyUpdated'))}
        {preset !== "all" && (
          <button style={{ ...btnStyle, color: "#b91c1c" }} onClick={() => setPreset("all")}>{t('formulaList.clear')}</button>
        )}
      </div>

      {/* ── Table ── */}
      {loadError && (
        <div style={{ border: "1px solid #cc0000", background: "#fff0f0", padding: "8px", marginBottom: "8px", fontSize: "12px", color: "#cc0000" }}>
          {t('compare.loadFailed', { defaultValue: 'Load failed' })}: {loadError}
        </div>
      )}
      {loading ? (
        <div style={{ color: "#666", padding: "20px", textAlign: "center", fontSize: "12px" }}>{t('common.loading')}</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ tableLayout: "fixed", width: visibleCols.reduce((s, c) => s + (colWidths[c.key] ?? 100), 0) + "px", borderCollapse: "collapse", border: "1px solid #333", background: "#fff" }}>
            <thead>
              <tr>
                {visibleCols.map((col) => (
                  <th key={col.key} style={thStyle(col)} onClick={() => handleSort(col.key)}>
                    {col.label}
                    {col.key !== "_star" && col.key !== "_actions" && (
                      <span style={{ color: col.key === sortCol ? "#000" : "#999", fontSize: "10px" }}>
                        {col.key === sortCol ? (sortDir === "asc" ? " \u25B2" : " \u25BC") : " \u25BD"}
                      </span>
                    )}
                    <span
                      onMouseDown={(e) => startResize(col.key, e)}
                      title="Drag to resize column"
                      style={{ position: "absolute", top: 0, right: 0, bottom: 0, width: "6px", cursor: "col-resize", background: "rgba(0,0,0,0.18)" }}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((f) => (
                <tr
                  key={f.formula_code}
                  onMouseEnter={(e) => { for (const cell of Array.from(e.currentTarget.cells)) cell.style.background = "#f5f5f5"; }}
                  onMouseLeave={(e) => { for (const cell of Array.from(e.currentTarget.cells)) cell.style.background = "#fff"; }}
                >
                  {visibleCols.map((col) => {
                    const k = listCommentKey(f.formula_code, col.key);
                    const hasComments = listCommentsByKey.has(k);
                    return (
                      <td
                        key={col.key}
                        style={{ ...getCellStyle(f, col.key), position: "relative" as const }}
                        onContextMenu={col.key !== "_open" && col.key !== "_star" && col.key !== "_actions" ? (e) => handleCellContextMenu(f.formula_code, col.key, e) : undefined}
                      >
                        {getCellValue(f, col.key)}
                        {hasComments && (
                          <span style={{
                            position: "absolute", top: 0, right: 0, width: 0, height: 0,
                            borderLeft: "6px solid transparent",
                            borderTop: "6px solid #3b82f6",
                          }} />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={visibleCols.length} style={{ padding: "20px", textAlign: "center", color: "#999", border: "1px solid #333", fontSize: "12px" }}>
                    {search.trim() ? t('formulaList.noFormulasMatch') : t('formulaList.noFormulasFound')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
